import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ServiceUtils } from '../service-utils/service-utils';
import {
    BASE_REPORT_URL_TOKEN,
    IdNameValueObject,
    Inspection,
    InspectionItem,
    InspectionSkipReason,
    RevokeConsolidatedLinksResult,
    SearchCriteria,
    SearchCriteriaItem,
} from '@raptor-fe/core';
import { LoadingHeaders } from '../web-loading-service/web-loading-headers';

@Injectable({
    providedIn: 'root',
})
export class InspectionApiService {
    private readonly http = inject(HttpClient);
    private readonly base_report_url = inject(BASE_REPORT_URL_TOKEN);
    private readonly serviceUtils = inject(ServiceUtils);

    private httpHeadersWithNoLoading = LoadingHeaders.httpHeadersWithNoLoading();

    /**
     * Translates a status filter key into the Search criteria that select inspections in that state.
     *
     * Every recognised status has an explicit branch. The default branch pushes nothing rather than
     * falling through to a completedDate filter: `status` is a plain string, so an unrecognised or
     * newly-added value must never be silently treated as "Completed" (the historical fall-through
     * hazard). A status the backend does not model yields no filter (equivalent to "all") instead of
     * a wrong result set.
     */
    applyStatusFilter(status: string, items: SearchCriteriaItem[]): void {
        switch (status) {
            case 'verified':
                items.push({ condition: 'and', name: 'lastVerifiedDate', operator: 'isNotNull', value: null });
                return;
            case 'active':
                items.push({ condition: 'and', name: 'completedDate', operator: 'isNull', value: null });
                return;
            case 'completed':
                items.push({ condition: 'and', name: 'completedDate', operator: 'isNotNull', value: null });
                return;
            case 'pending review':
                items.push({ condition: 'and', name: 'submittedForReviewDate', operator: 'isNotNull', value: null });
                items.push({ condition: 'and', name: 'completedDate', operator: 'isNull', value: null });
                return;
            case 'sent':
                items.push({ condition: 'and', name: 'sentDate', operator: 'isNotNull', value: null });
                return;
            case 'skipped':
                items.push({ condition: 'and', name: 'skippedAt', operator: 'isNotNull', value: null });
                return;
            case 'in progress':
                items.push({ condition: 'and', name: 'completedDate', operator: 'isNull', value: null });
                items.push({ condition: 'and', name: 'assignedDate', operator: 'isNull', value: null });
                return;
            case 'assigned':
                items.push({ condition: 'and', name: 'assignedDate', operator: 'isNotNull', value: null });
                items.push({ condition: 'and', name: 'completedDate', operator: 'isNull', value: null });
                return;
            case 'all':
            default:
                return;
        }
    }

    create(
        tenantId: string,
        branchId: string,
        customerId: string,
        templateId: string,
        vehicleId: string,
        technicianId: string | undefined,
        target: { targetQuoteId?: string; targetWorkOrderId?: string },
        notes?: string | null,
    ): Observable<Inspection> {
        const body = {
            customerId,
            templateId,
            vehicleId,
            technicianId,
            targetQuoteId: target.targetQuoteId,
            targetWorkOrderId: target.targetWorkOrderId,
            customerNotes: notes,
        };
        return this.http.post<Inspection>(this.serviceUtils.getBaseUrl('inspection', tenantId, branchId), body);
    }

    delete(tenantId: string, branchId: string, id: string): Observable<object> {
        return this.http.delete(`${this.serviceUtils.getBaseUrl('inspection', tenantId, branchId)}/${id}`);
    }

    get(tenantId: string, branchId: string, id: string): Observable<Inspection | null> {
        return this.http.get<Inspection>(`${this.serviceUtils.getBaseUrl('inspection', tenantId, branchId)}/${id}`);
    }

    getByCustomerId(tenantId: string, branchId: string, customerId: string): Observable<Inspection[]> {
        return this.http.get<Inspection[]>(`${this.serviceUtils.getBaseUrl('inspection', tenantId, branchId)}/customer/${customerId}`);
    }

    getByUser(tenantId: string, branchId: string, isCompleted: boolean, sortBy?: string, pageSize = 20, pageNumber = 1): Observable<Inspection[]> {
        return this.http.get<Inspection[]>(
            `${this.serviceUtils.getBaseUrl('inspection', tenantId, branchId)}/get-by-user?isCompleted=${isCompleted}&sortBy=${sortBy}&pageSize=${pageSize}&pageNumber=${pageNumber}`,
        );
    }

    /**
     * Fetches AI-generated notes with their AI-generated recommendations attached in one response.
     * Used by the inspection picker so AI notes show recs without a second round-trip per note.
     * Pass `showLoading=false` to bypass the global loading interceptor (callers that render their
     * own inline spinner -- e.g., the catalog modal -- should opt out so the AI fetch does not
     * block the modal or surrounding app while typing).
     */
    getAINotes(
        tenantId: string,
        branchId: string,
        sectionName: string,
        itemName: string,
        keyword?: string,
        showLoading = true,
    ): Observable<{ id: string; note: string; recommendations: string[] }[]> {
        const body = { sectionName, itemName, pageSize: 10, keyword };
        return this.http.post<{ id: string; note: string; recommendations: string[] }[]>(
            this.serviceUtils.getBaseUrl('inspection', tenantId, branchId, 'v2') + '/item-note-suggestions/ai',
            body,
            { headers: showLoading ? undefined : this.httpHeadersWithNoLoading },
        );
    }

    /**
     * Fetches AI-generated recommendations for a given note context. Pass `showLoading=false`
     * to bypass the global loading interceptor (callers that render their own inline spinner
     * should opt out so the AI fetch does not block the modal or surrounding app while typing).
     */
    getAIRecommendations(
        tenantId: string,
        branchId: string,
        sectionName: string,
        itemName: string,
        notes: string[],
        keyword?: string,
        showLoading = true,
    ): Observable<IdNameValueObject[]> {
        const body = { sectionName, itemName, notes, pageSize: 10, keyword };
        return this.http.post<IdNameValueObject[]>(
            this.serviceUtils.getBaseUrl('inspection', tenantId, branchId) + '/item-recommendation-suggestions/ai',
            body,
            { headers: showLoading ? undefined : this.httpHeadersWithNoLoading },
        );
    }

    getItemKnowledgeHint(
        tenantId: string,
        branchId: string,
        sectionName: string,
        itemName: string,
    ): Observable<{ description: string; impact: string }> {
        return this.http.post<{ description: string; impact: string }>(`${this.serviceUtils.getBaseUrl('inspection', tenantId, branchId)}/item-knowledge-hint`, {
            sectionName,
            itemName,
        });
    }

    getReport(tenantId: string, branchId: string, id: string, showLoading = true): Observable<{ url: string; expiresOn: string | null }> {
        return this.http.get<{ url: string; expiresOn: string | null }>(`${this.getBaseReportUrl(tenantId, branchId)}/${id}`, {
            headers: showLoading ? undefined : this.httpHeadersWithNoLoading,
        });
    }

    search(tenantId: string, branchId: string, criteria: SearchCriteria, showLoading = true): Observable<Inspection[]> {
        return this.http.post<Inspection[]>(this.serviceUtils.getBaseUrl('inspection', tenantId, branchId) + '/search', criteria, {
            headers: showLoading ? undefined : this.httpHeadersWithNoLoading,
        });
    }

    searchByKeyword(
        tenantId: string,
        branchId: string,
        keyword: string,
        status: 'active' | 'completed' | 'all',
        pageNumber: number,
        pageSize: number,
        showLoading = true,
    ): Observable<Inspection[]> {
        const criteria = { pageSize, pageNumber, items: [], orderBy: 'name' } as SearchCriteria;

        this.applyStatusFilter(status, criteria.items);

        return this.http.post<Inspection[]>(this.serviceUtils.getBaseUrl('inspection', tenantId, branchId) + `/search/keyword/${keyword}`, criteria, {
            headers: showLoading ? undefined : this.httpHeadersWithNoLoading,
        });
    }

    /**
     * Transfers a completed inspection to its target quote or work order. Targets the v2 endpoint,
     * which enforces the advisor pre-transfer review gate server-side (v1 remains the deprecated,
     * gate-free contract for already-shipped clients). Both web and mobile stores call this shared
     * method, so they adopt the gated v2 behaviour together.
     */
    transfer(tenantId: string, branchId: string, id: string): Observable<Inspection | null> {
        return this.http.post<Inspection>(`${this.serviceUtils.getBaseUrl('inspection', tenantId, branchId, 'v2')}/transfer/${id}`, {});
    }

    /**
     * Submits a completed inspection for advisor review, moving it out of the technician's queue into
     * the pending-review state. Technician-initiated (backend gates on Inspection.Update). This is a new
     * endpoint with no v1 predecessor, so it resolves to the controller's default v1 route. Returns the
     * updated inspection so the caller can patch local state without a follow-up fetch.
     */
    submitForReview(tenantId: string, branchId: string, id: string): Observable<Inspection | null> {
        return this.http.post<Inspection>(`${this.serviceUtils.getBaseUrl('inspection', tenantId, branchId)}/submit-for-review/${id}`, {});
    }

    /**
     * Records the technician signature that attests the work on a handback inspection is complete. The
     * PNG is uploaded through the file service first; this call stores only its internal blob name, and
     * the server stamps who signed and when. Returns the updated inspection so the caller can patch local
     * state before transferring.
     */
    recordTechnicianSignature(tenantId: string, branchId: string, inspectionId: string, signatureImageUrl: string): Observable<Inspection> {
        return this.http.post<Inspection>(`${this.serviceUtils.getBaseUrl('inspection', tenantId, branchId)}/technician-signature/${inspectionId}`, {
            signatureImageUrl,
        });
    }

    /**
     * Reopens an inspection that is pending advisor review, returning it to the technician for further
     * work. Advisor-initiated (backend gates on Inspection.Review). This is a new endpoint with no v1
     * predecessor, so it resolves to the controller's default v1 route. Returns the updated inspection
     * so the caller can patch local state without a follow-up fetch.
     */
    reopenForTechnician(tenantId: string, branchId: string, id: string): Observable<Inspection | null> {
        return this.http.post<Inspection>(`${this.serviceUtils.getBaseUrl('inspection', tenantId, branchId)}/reopen-for-technician/${id}`, {});
    }

    /**
     * Fetches the tenant-curated list of reasons a technician may select when skipping an inspection.
     * Only reasons the backend considers selectable are returned; callers should not filter further.
     *
     * With `inspectionId` the server returns the list for that inspection's type; without it, General.
     */
    getSkipReasons(tenantId: string, branchId: string, inspectionId?: string | null): Observable<InspectionSkipReason[]> {
        const url = `${this.serviceUtils.getBaseUrl('InspectionSkipReason', tenantId)}/effective/${branchId}`;
        return this.http.get<InspectionSkipReason[]>(url, inspectionId ? { params: { inspectionId } } : undefined);
    }

    /**
     * Skips an inspection with the selected reason code, bypassing item-by-item completion. The optional
     * note carries free-text context the technician adds alongside the reason. Returns the updated
     * inspection so the caller can patch its local copy without a follow-up fetch.
     */
    skip(tenantId: string, branchId: string, id: string, reasonCode: string, note?: string): Observable<Inspection> {
        return this.http.post<Inspection>(`${this.serviceUtils.getBaseUrl('inspection', tenantId, branchId)}/${id}/skip`, { reasonCode, note });
    }

    save(tenantId: string, branchId: string, inspection: Inspection): Observable<Inspection> {
        return this.http.put<Inspection>(`${this.serviceUtils.getBaseUrl('inspection', tenantId, branchId)}`, inspection);
    }

    updateItemKnowledge(tenantId: string, branchId: string, itemId: string, showLoading = true): Observable<InspectionItem | null> {
        return this.http.put<InspectionItem>(
            `${this.serviceUtils.getBaseUrl('inspection', tenantId, branchId)}/item-knowledge/item/${itemId}`,
            {},
            {
                headers: showLoading ? undefined : this.httpHeadersWithNoLoading,
            },
        );
    }

    /**
     * Creates a new check-in inspection with the selected template.
     * Called after advisor selects a template in the check-in flow.
     * @param tenantId - The tenant ID
     * @param branchId - The branch ID
     * @param templateId - The selected inspection template ID
     * @param appointmentId - The appointment ID
     * @param customerId - The customer ID
     * @param vehicleId - The vehicle ID
     * @param advisorId - The advisor user ID
     * @returns Observable of created inspection
     */
    createCheckInInspection({
        tenantId,
        branchId,
        templateId,
        appointmentId,
        customerId,
        vehicleId,
        advisorId,
    }: {
        tenantId: string;
        branchId: string;
        templateId: string;
        appointmentId: string;
        customerId: string;
        vehicleId: string;
        advisorId: string;
    }): Observable<Inspection> {
        const body = {
            templateId,
            appointmentId,
            customerId,
            vehicleId,
            advisorId,
        };
        return this.http.post<Inspection>(`${this.serviceUtils.getBaseUrl('inspection', tenantId, branchId)}/checkin`, body);
    }

    getConsolidatedReportForQuote<T = unknown>(tenantId: string, branchId: string, quoteId: string): Observable<T> {
        return this.http.get<T>(
            `${this.serviceUtils.getBaseUrl('inspection', tenantId, branchId)}/consolidated-report/quote/${quoteId}`,
        );
    }

    getConsolidatedReportForWorkOrder<T = unknown>(tenantId: string, branchId: string, workOrderId: string): Observable<T> {
        return this.http.get<T>(
            `${this.serviceUtils.getBaseUrl('inspection', tenantId, branchId)}/consolidated-report/work-order/${workOrderId}`,
        );
    }

    shareConsolidatedForQuote(tenantId: string, branchId: string, quoteId: string): Observable<object> {
        return this.http.post(
            `${this.serviceUtils.getBaseUrl('inspection', tenantId, branchId)}/share-consolidated/quote/${quoteId}`,
            {},
        );
    }

    shareConsolidatedForWorkOrder(tenantId: string, branchId: string, workOrderId: string): Observable<object> {
        return this.http.post(
            `${this.serviceUtils.getBaseUrl('inspection', tenantId, branchId)}/share-consolidated/work-order/${workOrderId}`,
            {},
        );
    }

    /**
     * Revokes every customer link issued for the quote: the consolidated approval link and the inspection
     * PDF download links. Links already sent stop working; sharing again issues fresh ones.
     */
    revokeConsolidatedLinksForQuote(tenantId: string, branchId: string, quoteId: string): Observable<RevokeConsolidatedLinksResult> {
        return this.http.post<RevokeConsolidatedLinksResult>(
            `${this.serviceUtils.getBaseUrl('inspection', tenantId, branchId)}/revoke-consolidated-links/quote/${quoteId}`,
            {},
        );
    }

    /**
     * Revokes every customer link issued for the work order: the consolidated approval link and the
     * inspection PDF download links. Links already sent stop working; sharing again issues fresh ones.
     */
    revokeConsolidatedLinksForWorkOrder(tenantId: string, branchId: string, workOrderId: string): Observable<RevokeConsolidatedLinksResult> {
        return this.http.post<RevokeConsolidatedLinksResult>(
            `${this.serviceUtils.getBaseUrl('inspection', tenantId, branchId)}/revoke-consolidated-links/work-order/${workOrderId}`,
            {},
        );
    }

    submitConsolidatedApprovalForQuote(
        tenantId: string,
        branchId: string,
        quoteId: string,
        approvedItems: string[],
        approvedItemLines: string[],
    ): Observable<{ confirmationId: string }> {
        return this.http.post<{ confirmationId: string }>(
            `${this.serviceUtils.getBaseUrl('inspection', tenantId, branchId)}/consolidated-approval/quote/${quoteId}`,
            { approvedQuoteItems: approvedItems, approvedQuoteItemLines: approvedItemLines },
        );
    }

    submitConsolidatedApprovalForWorkOrder(
        tenantId: string,
        branchId: string,
        workOrderId: string,
        approvedItems: string[],
        approvedItemLines: string[],
    ): Observable<{ confirmationId: string }> {
        return this.http.post<{ confirmationId: string }>(
            `${this.serviceUtils.getBaseUrl('inspection', tenantId, branchId)}/consolidated-approval/work-order/${workOrderId}`,
            { approvedQuoteItems: approvedItems, approvedQuoteItemLines: approvedItemLines },
        );
    }

    private getBaseReportUrl(tenantId: string, branchId: string, version = 'v1'): string {
        return `${this.base_report_url}${version}/tenant/${tenantId}/branch/${branchId}/report/inspection`;
    }
}
